import { useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    FormControl,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataTable } from '@components/DataTable';
import { DataTableColumn, SortModel } from '@components/DataTable/types';
import { SearchBarWithNumber } from '@components/SearchbarWithNumber';
import { ScoringMode, ScoringResultItem, ScoringStatus } from '../types';
import { ScoringScoreBadge } from './ScoringScoreBadge';
import { ScoringExplainerModal } from './ScoringExplainerModal';
import { useDebounce } from '@hooks/useDebounce';

const MODE_LABELS: Record<ScoringMode, string> = {
    many_to_one: 'Talents sélectionnés → 1 Offre',
    one_to_many: '1 Talent → Plusieurs offres',
    all_to_one: 'Talents → 1 Offre',
    one_to_all: '1 Talent → Offres',
    one_to_one: '1 Talent → 1 Offre',
};

type StatusFilterValue = 'all' | ScoringStatus;

const STATUS_LABELS: Record<StatusFilterValue, string> = {
    all: 'Tous les statuts',
    pending_cv: 'En attente de CV',
    pending: 'En attente',
    in_progress: 'En cours',
    done: 'Terminé',
    error: 'Erreur',
};

interface ScoringResultsTableProps {
    results: ScoringResultItem[];
    loading?: boolean;
}

export const ScoringResultsTable = ({ results, loading }: ScoringResultsTableProps) => {
    const theme = useTheme();
    const [selectedResult, setSelectedResult] = useState<ScoringResultItem | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<StatusFilterValue>('all');
    const [sortModel, setSortModel] = useState<SortModel[]>([]);
    const [searchQuery, setSearchQuery] = useState('');

    const normalizedSearchQuery = useDebounce(searchQuery.trim().toLowerCase(), 500);

    const filteredResults = useMemo(
        () => {

            return results.filter((result) => {
                if (selectedStatus !== 'all' && result.status !== selectedStatus) {
                    return false;
                }

                const talentName = result.talentInfo?.fullName?.toLowerCase() ?? '';
                const talentEmail = result.talentInfo?.email?.toLowerCase() ?? '';
                const jobTitle = result.jobOfferInfo?.title?.toLowerCase() ?? '';
                const jobNumber = String(result.jobOfferInfo?.jobNumber ?? '').toLowerCase();

                if (!normalizedSearchQuery) {
                    return true;
                }

                return [talentName, talentEmail, jobTitle, jobNumber].some((value) =>
                    value.includes(normalizedSearchQuery)
                );
            });
        },
        [results, selectedStatus, normalizedSearchQuery]
    );

    const columns: DataTableColumn<ScoringResultItem>[] = useMemo(
        () => [
            {
                id: 'talentInfo',
                label: 'Talent / Favori',
                field: 'talentInfo',
                minWidth: '220px',
                sortable: true,
                sortComparator: (a, b) => {
                    const aName = a?.fullName?.toLowerCase?.() ?? '';
                    const bName = b?.fullName?.toLowerCase?.() ?? '';
                    return aName.localeCompare(bName);
                },
                renderCell: ({ row }) => {
                    const info = row.talentInfo;
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Avatar
                                src={info?.photo ?? undefined}
                                alt={info?.fullName ?? undefined}
                                sx={{ width: 36, height: 36, flexShrink: 0 }}
                            />
                            <Box sx={{ overflow: 'hidden' }}>
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    noWrap
                                    sx={{ color: theme.palette.text.primary }}>
                                    {info?.fullName ?? '—'}
                                </Typography>
                                {info?.email && (
                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{ color: theme.palette.text.secondary }}>
                                        {info.email}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                },
            },
            {
                id: 'jobOfferInfo',
                label: "Offre d'emploi",
                field: 'jobOfferInfo',
                minWidth: '220px',
                sortable: true,
                sortComparator: (a, b) => {
                    const aTitle = a?.title?.toLowerCase?.() ?? '';
                    const bTitle = b?.title?.toLowerCase?.() ?? '';
                    return aTitle.localeCompare(bTitle);
                },
                renderCell: ({ row }) => {
                    const info = row.jobOfferInfo;
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Avatar
                                src={info?.logo ?? undefined}
                                variant="rounded"
                                sx={{ width: 36, height: 36, flexShrink: 0 }}
                            />
                            <Box sx={{ overflow: 'hidden' }}>
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    noWrap
                                    sx={{ color: theme.palette.text.primary }}>
                                    {info?.title ?? '—'}
                                </Typography>
                                {info?.jobNumber && (
                                    <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{ color: theme.palette.text.secondary }}>
                                        #{info.jobNumber}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                },
            },
            {
                id: 'score',
                label: 'Score',
                field: 'score',
                minWidth: '100px',
                align: 'center',
                sortable: true,
                sortComparator: (a, b) => (a ?? -1) - (b ?? -1),
                renderCell: ({ row }) => (
                    <ScoringScoreBadge
                        score={row.score}
                        status={row.status}
                        onClick={
                            row.status === 'done' && row.explainer
                                ? () => setSelectedResult(row)
                                : undefined
                        }
                    />
                ),
            },
            {
                id: 'mode',
                label: 'Mode',
                field: 'mode',
                minWidth: '160px',
                sortable: true,
                sortComparator: (a, b) => {
                    const aLabel = MODE_LABELS[a as ScoringMode] ?? (a as string) ?? '';
                    const bLabel = MODE_LABELS[b as ScoringMode] ?? (b as string) ?? '';
                    return aLabel.localeCompare(bLabel);
                },
                renderCell: ({ row }) => MODE_LABELS[row.mode] ?? row.mode,
            },
            {
                id: 'retryCount',
                label: 'Tentatives',
                field: 'retryCount',
                minWidth: '100px',
                align: 'center',
                sortable: true,
                renderCell: ({ row }) => row.retryCount,
            },
        ],
        [theme]
    );

    const handleStatusChange = (event: SelectChangeEvent<StatusFilterValue>) => {
        setSelectedStatus(event.target.value as StatusFilterValue);
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 2,
                }}>
                <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 320 } }}>
                    <SearchBarWithNumber
                        onChange={setSearchQuery}
                        placeholder="Rechercher talent, email, titre ou numéro d'offre"
                        totalCount={filteredResults.length}
                        fullWidth
                    />
                </Box>

                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <Select value={selectedStatus} onChange={handleStatusChange}>
                        {Object.entries(STATUS_LABELS).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <DataTable
                rows={filteredResults}
                columns={columns}
                loading={loading}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                height="calc(100vh - 520px)"
                stickyHeader
            />
            <ScoringExplainerModal
                open={!!selectedResult}
                onClose={() => setSelectedResult(null)}
                result={selectedResult}
            />
        </Box>
    );
};

export default ScoringResultsTable;
